// Portal detection alerts on the case timeline.
//
// The case's All Events timeline is built from the investigation run blob. The alerts
// the portal itself raised about the same person live in `DetectionAlerts` and were
// never on it, so an analyst who opened a case FROM an alert could not see that alert,
// or the others about the same user, beside the activity that caused them.
//
// These rows are fetched at view time (`fetchCaseUserAlerts`), not carried in the run
// blob, so they need no re-run and always show the alert's current status. For the same
// reason they are NOT part of the reports: the report streams are reconciled against the
// pinned run blob, and a row from outside it would break that.
//
// Pure JS, no React: imported by CaseDetail, TimelineView and eventUtils.

import { normalizeIsoUtc } from './timeFormat.js';

export const CASE_ALERT_EVENT_TYPE = 'DetectionAlert';

// The product's formal name, used wherever these rows say where the alert came from.
export const CASE_ALERT_SOURCE_NAME = 'Blue 365 Signal';
export const CASE_ALERT_LABEL = `${CASE_ALERT_SOURCE_NAME} Alert`;

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * The case window in UTC: from the start of the start date to the END of the end date
 * (`to` exclusive). Null when either date is missing or unparseable.
 */
export function caseAlertWindow(startDate, endDate) {
  const s = String(startDate || '').substring(0, 10);
  const e = String(endDate || '').substring(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s) || !/^\d{4}-\d{2}-\d{2}$/.test(e)) return null;
  const from = Date.parse(`${s}T00:00:00Z`);
  const to = Date.parse(`${e}T00:00:00Z`) + DAY_MS;
  if (Number.isNaN(from) || Number.isNaN(to) || to <= from) return null;
  return { from: new Date(from).toISOString(), to: new Date(to).toISOString() };
}

/**
 * Who the case is about: the run's resolved identifiers, its Entra object id, and the
 * address the case was opened under. Lowercased and de-duplicated. The object id
 * matters because some alerts record the user as a GUID.
 */
export function caseAlertIdentifiers(userPrincipalName, identity) {
  const out = new Set();
  const add = (v) => { const s = String(v || '').trim().toLowerCase(); if (s) out.add(s); };
  add(userPrincipalName);
  (identity?.Identifiers || []).forEach(add);
  add(identity?.EntraObjectId);
  return [...out];
}

/** The timeline row's EventId. Prefixed so it can never collide with a log event id. */
export function caseAlertEventId(alertId) {
  return `portal-alert:${alertId}`;
}

// Canonical `…Z` ISO (millisecond precision), or '' when unparseable. The alert table
// writes `+00:00` offsets and 6-digit fractions; the timeline's other rows are `…Z`, and
// a mixed shape would mis-order under a string sort.
function canonicalIso(ts) {
  const t = Date.parse(normalizeIsoUtc(ts || ''));
  return Number.isNaN(t) ? '' : new Date(t).toISOString();
}

function parseJsonSafe(s) {
  if (!s || typeof s !== 'string') return null;
  try { return JSON.parse(s); } catch { return null; }
}

/**
 * One GetAlerts row → one All Events row. Placed at the EVENT time (when the activity
 * happened), so it sits beside the event that triggered it; the time the alert was
 * raised is kept on `_portalAlert.alertTime` for the expanded row.
 */
export function toCaseAlertEvent(alert) {
  const a = alert || {};
  const when = canonicalIso(a.eventTime) || canonicalIso(a.alertTime);
  return {
    EventId: caseAlertEventId(a.alertId),
    EventTimeUTC: when,
    UserPrincipalName: a.userId || '',
    IPAddress: a.clientIp || '',
    Operation: a.ruleName || CASE_ALERT_LABEL,
    ApplicationId: a.applicationId || '',
    AppDisplayName: a.appDisplayName || '',
    RawLog: parseJsonSafe(a.rawEventJson),
    _eventType: CASE_ALERT_EVENT_TYPE,
    _category: CASE_ALERT_EVENT_TYPE,
    _portalAlert: {
      alertId: a.alertId || '',
      tenantId: a.tenantId || '',
      ruleName: a.ruleName || '',
      severity: a.severity || '',
      status: a.status || '',
      alertTime: canonicalIso(a.alertTime),
      eventTime: canonicalIso(a.eventTime),
      logType: a.logType || '',
      operation: a.operation || '',
      userId: a.userId || '',
      matchBasis: a.caseMatchBasis || '',
      closeReason: a.closeReason || '',
      closedBy: a.closedBy || '',
      closedUtc: a.closedUtc || '',
      psaTicketNumber: a.psaTicketNumber || a.psaTicketId || '',
    },
  };
}

/** Rows sorted oldest first, alerts with no usable time dropped. */
export function toCaseAlertEvents(alerts) {
  return (Array.isArray(alerts) ? alerts : [])
    .map(toCaseAlertEvent)
    .filter((e) => e.EventTimeUTC && !Number.isNaN(Date.parse(e.EventTimeUTC)))
    .sort((x, y) => Date.parse(x.EventTimeUTC) - Date.parse(y.EventTimeUTC));
}

/**
 * The All Events description. ONE helper, called by both copies of generateDescription
 * (TimelineView's and eventUtils'), so the two cannot word the same alert differently.
 */
export function describeCaseAlert(event) {
  const pa = event?._portalAlert || {};
  const severity = pa.severity ? `[${String(pa.severity).toUpperCase()}]` : '';
  const title = pa.ruleName || event?.Operation || CASE_ALERT_LABEL;
  const status = pa.status ? `(${pa.status})` : '';
  const about = pa.matchBasis === 'named' && pa.userId ? `— alert on ${pa.userId}` : '';
  return [severity, title, status, about].filter(Boolean).join(' ');
}

/** Deep link to the alert on the Alerts page. */
export function caseAlertHref(event) {
  const pa = event?._portalAlert || {};
  if (!pa.tenantId || !pa.alertId) return '';
  return `/alerts/${encodeURIComponent(pa.tenantId)}/${encodeURIComponent(pa.alertId)}`;
}
